import React, { useState, useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import './DataTable.css';

export interface Column<T = any> {
  key: string;
  title: string;
  sortable?: boolean;
  searchable?: boolean;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  // text the column search matches against, for columns whose value is an
  // object (e.g. a user); defaults to the raw value
  searchValue?: (row: T) => string;
  // value the column sorts by; defaults to the raw value
  sortValue?: (row: T) => any;
  width?: string;
}

interface DataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  pageSize?: number;
  loading?: boolean;
  rowKey?: string | ((row: T) => string | number);
  className?: string;
}

type SortDir = 'asc' | 'desc' | null;

function getVal(obj: any, path: string): any {
  return path.split('.').reduce((acc, k) => acc?.[k], obj);
}

// Persian / Arabic digits typed in a search box match stored Latin digits
const normalizeDigits = (text: string) =>
  text
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

function DataTable<T = any>({
  columns,
  data,
  pageSize = 10,
  loading = false,
  rowKey,
  className = '',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [colSearch, setColSearch] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDir === 'asc') { setSortDir('desc'); }
      else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleSearch = (key: string, value: string) => {
    setColSearch(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const processed = useMemo(() => {
    let rows = [...data];

    // Per-column filtering
    for (const [key, val] of Object.entries(colSearch)) {
      if (!val.trim()) continue;
      const lv = normalizeDigits(val.trim().toLowerCase());
      const col = columns.find((c) => c.key === key);
      rows = rows.filter((row) => {
        const text = col?.searchValue ? col.searchValue(row) : getVal(row, key);
        return normalizeDigits(String(text ?? '').toLowerCase()).includes(lv);
      });
    }

    // Sorting
    if (sortKey && sortDir) {
      const sortCol = columns.find((c) => c.key === sortKey);
      const value = (row: T) =>
        sortCol?.sortValue ? sortCol.sortValue(row) : getVal(row, sortKey);
      rows.sort((a, b) => {
        const av = value(a);
        const bv = value(b);
        if (av === bv) return 0;
        const cmp = av < bv ? -1 : 1;
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [data, columns, colSearch, sortKey, sortDir]);

  const totalPages = Math.ceil(processed.length / pageSize);
  const pageRows = processed.slice(page * pageSize, (page + 1) * pageSize);

  const sortIcon = (key: string) => {
    if (sortKey !== key) return '↕';
    return sortDir === 'asc' ? '↑' : '↓';
  };

  const getRowKey = (row: T, index: number): string | number => {
    if (!rowKey) return index;
    if (typeof rowKey === 'function') return rowKey(row);
    return String(getVal(row, rowKey));
  };

  return (
    <div className={`dt-wrapper ${className}`}>
      <div className="dt-scroll-container">
        <table className="dt-table">
          <thead>
            {/* Column headers */}
            <tr className="dt-header-row">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`dt-th${col.sortable !== false ? ' dt-sortable' : ''}${sortKey === col.key ? ' dt-sorted' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                >
                  <span className="dt-th-content">
                    {col.title}
                    {col.sortable !== false && (
                      <em className="dt-sort-icon">{sortIcon(col.key)}</em>
                    )}
                  </span>
                </th>
              ))}
            </tr>
            {/* Per-column search row */}
            <tr className="dt-search-row">
              {columns.map(col => (
                <th key={col.key + '-s'} className="dt-search-th">
                  {col.searchable !== false && (
                    <input
                      className="dt-search-input"
                      placeholder="جستجو..."
                      value={colSearch[col.key] || ''}
                      onChange={e => handleSearch(col.key, e.target.value)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="dt-loading">در حال بارگذاری...</td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="dt-empty">داده‌ای یافت نشد</td>
              </tr>
            ) : (
              pageRows.map((row, index) => (
                <tr key={getRowKey(row, index)} className="dt-row">
                  {columns.map(col => (
                    <td key={col.key} className="dt-td">
                      {col.render
                        ? col.render(getVal(row, col.key), row, index)
                        : (getVal(row, col.key) ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <ReactPaginate
          pageCount={totalPages}
          pageRangeDisplayed={5}
          marginPagesDisplayed={1}
          forcePage={page}
          onPageChange={({ selected }) => setPage(selected)}
          containerClassName="dt-pagination"
          pageClassName="dt-page-item"
          pageLinkClassName="dt-page-link"
          activeClassName="dt-page-active"
          disabledClassName="dt-page-disabled"
          previousLabel="›"
          nextLabel="‹"
          previousClassName="dt-page-item"
          nextClassName="dt-page-item"
          breakLabel="..."
          breakClassName="dt-page-item"
        />
      )}
    </div>
  );
}

export default DataTable;
